import { type ImgHTMLAttributes, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import {
  type OptimizedWidth,
  getLqipUrl,
  getOptimizedImageUrl,
  getOptimizedSrcSet,
} from '@/utils/image-optimization';

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  /** Raw image URL (Supabase public object URL, data:, blob:, or 3rd-party). */
  src: string | null | undefined;
  /** Target render width in CSS pixels (the @1× width). One of the discrete tiers. */
  width: OptimizedWidth;
  /** Target render height in CSS pixels. Defaults to `width` for square cells. */
  height?: number;
  /**
   * The `sizes` attribute. Tells the browser the displayed size at each
   * breakpoint so it can pick the right srcset variant. Required for
   * responsive images — without it, browsers conservatively pick the largest.
   */
  sizes?: string;
  /** JPEG/WebP quality 1-100. Default 75 for thumbnails, 85 for full-views. */
  quality?: number;
  /** Resize mode for Supabase transform. */
  resize?: 'cover' | 'contain';
  /**
   * Apply the Supabase/Vercel transformation pipeline. Default true. Set false
   * for a variant that was already resized and encoded during ingestion.
   */
  transform?: boolean;
  /**
   * Render an LQIP (low-quality image placeholder) blur as the background
   * while the real image loads. Crossfades when the real image decodes.
   * Default true. Set false for inline icon-sized images where the LQIP
   * roundtrip costs more than the placeholder is worth.
   */
  lqip?: boolean;
  /** Fallback rendered when src is null or the image fails to load. */
  fallback?: React.ReactNode;
  /**
   * Loading mode. Default `lazy` — only fetches when within viewport
   * proximity. Set `eager` for hero images / above-the-fold critical
   * content (LCP elements).
   */
  loading?: 'lazy' | 'eager';
  /**
   * Decode priority. Default `async` keeps decode off the main thread (no
   * scroll jank). Set `auto` for above-the-fold images where you want the
   * browser to decide.
   */
  decoding?: 'async' | 'sync' | 'auto';
  /**
   * CSS object-fit applied to the inner `<img>`. Default `'cover'`. Set
   * `'contain'` for full-frame previews (e.g. MediaPreviewDialog).
   */
  objectFit?: 'cover' | 'contain';
  /**
   * Classes appended to the inner `<img>`. Use this for object-position,
   * group-hover transforms, blend modes — anything CSS that has to live on
   * the actual image element. The outer `className` goes to the wrapper
   * (which holds the skeleton + LQIP layers).
   */
  imgClassName?: string;
}

/**
 * The canonical image primitive for Mora.
 *
 * Renders a `<span>` wrapper holding (a) a skeleton/LQIP layer that's always
 * visible until the real image decodes, and (b) the `<img>` itself which
 * fades in over the placeholder. The wrapper carries the layout classes
 * (size, aspect, border) — the img inside is absolutely positioned and
 * fills the box.
 *
 * Why the wrapper: C3-2 + C5-3 — the previous img-only return set the LQIP
 * as `background-image` on the same element it then faded to opacity 0 →
 * during loading, the background was hidden too. Library + curated Faces
 * grids rendered as solid muted rectangles for the entire fetch window.
 *
 * Behavior:
 *   - Hybrid Path C URL transform (Supabase render → Vercel /_vercel/image
 *     in prod, Supabase render direct in dev).
 *   - Responsive srcset with 1×/2× variants.
 *   - Native loading="lazy" + decoding="async" by default.
 *   - Explicit width/height to prevent CLS.
 *   - Skeleton background ALWAYS visible until load (mora_design_system
 *     skeleton discipline: `bg-muted/40` + `animate-pulse`).
 *   - LQIP blur background takes over the skeleton when available.
 *   - Fallback rendered when src is null or fetch errors.
 *
 * API:
 *   - `className` → wrapper (size, aspect, border, custom layout)
 *   - `imgClassName` → img (object-position, transforms, blend modes)
 *   - `objectFit` → img CSS (default 'cover')
 *
 * Production reference: this is the same pattern Stripe Dashboard, Notion,
 * and Apple Photos web ship for their gallery surfaces.
 *
 * Audit + measurements: tasks/2026-05-02-library-image-perf-analysis.md
 */
export function OptimizedImage({
  src,
  width,
  height,
  sizes,
  quality,
  resize = 'cover',
  transform = true,
  lqip = true,
  fallback,
  loading = 'lazy',
  decoding = 'async',
  objectFit = 'cover',
  imgClassName,
  className,
  alt = '',
  onLoad,
  onError,
  ...rest
}: OptimizedImageProps) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [loadableSrc, setLoadableSrc] = useState<string | null>(() =>
    loading === 'eager' ? (src ?? null) : null,
  );

  useEffect(() => {
    setFailed(false);
    setLoaded(false);

    if (!src) {
      setLoadableSrc(null);
      return;
    }
    if (loading === 'eager' || typeof IntersectionObserver === 'undefined') {
      setLoadableSrc(src);
      return;
    }

    // Do not attach a transformed URL to an offscreen <img>. Browser-native
    // lazy loading intentionally prefetches far below the viewport, which is
    // too broad when billing counts each distinct transformed origin.
    setLoadableSrc(null);
    const target = wrapperRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setLoadableSrc(src);
        observer.disconnect();
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loading, src]);

  if (!src || failed) {
    return <>{fallback ?? null}</>;
  }

  const optimizedSrc = transform ? getOptimizedImageUrl(src, { width, quality, resize }) : src;
  const srcSet = transform ? getOptimizedSrcSet(src, width, resize) : '';
  const lqipSrc = transform && lqip ? getLqipUrl(src) : null;
  const shouldLoad = loadableSrc === src;

  return (
    <span ref={wrapperRef} className={cn('relative block overflow-hidden bg-muted/30', className)}>
      {/* Skeleton + LQIP layer — rendered until the real image decodes. Keep
          the LQIP as an actual lazy image: a CSS background requests every
          rendered origin immediately, including cards below the viewport. */}
      {!loaded && (
        <span
          aria-hidden
          className={cn('absolute inset-0', lqipSrc ? '' : 'bg-muted/40 animate-pulse')}
        >
          {lqipSrc && shouldLoad ? (
            <img
              src={lqipSrc}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="size-full object-cover"
            />
          ) : null}
        </span>
      )}
      {shouldLoad ? (
        <img
          src={optimizedSrc}
          srcSet={srcSet || undefined}
          sizes={sizes}
          width={width}
          height={height ?? width}
          loading={loading}
          decoding={decoding}
          alt={alt}
          className={cn(
            'absolute inset-0 size-full transition-opacity duration-200',
            objectFit === 'contain' ? 'object-contain' : 'object-cover',
            loaded ? 'opacity-100' : 'opacity-0',
            imgClassName,
          )}
          // A cached image can finish loading — `.complete` true,
          // `naturalWidth` real — before React's `onLoad` listener attaches,
          // so `onLoad` never fires and `loaded` stays false forever: the
          // skeleton overlay never lifts and the cell reads as permanently
          // blank. Verified live, 2026-09-16: a grid of already-cached
          // library thumbnails loaded every underlying <img> (complete:
          // true, naturalWidth: 512) while every cell stayed gray. This ref
          // catches that race by checking `.complete` the instant the node
          // mounts, in addition to the normal onLoad path for a genuinely
          // fresh fetch.
          ref={(node) => {
            if (node?.complete) setLoaded(true);
          }}
          onLoad={(e) => {
            setLoaded(true);
            onLoad?.(e);
          }}
          onError={(e) => {
            setFailed(true);
            onError?.(e);
          }}
          {...rest}
        />
      ) : null}
    </span>
  );
}
